// Invoice / Quote designer. White-labels every customer-facing document
// (invoices, credit notes, quotes) by saving a template to
// dodobooks_invoice_templates. Live preview pane on the right shows
// exactly how the next sent invoice will look.

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Upload, Image as ImageIcon, Palette, Type, Eye, Save, RotateCcw, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney } from '../lib/format';
import type { InvoiceTemplate, InvoiceTemplateSettings, Organization } from '../lib/types';

const DEFAULTS: InvoiceTemplateSettings = {
  logo_url: null,
  banner_url: null,
  primary_color: '#0c8f60',
  accent_color: '#d6a85a',
  paper_color: '#f5efe0',
  text_color: '#0f1521',
  font_family: 'Fraunces',
  title_invoice: 'Invoice',
  title_credit_note: 'Credit Note',
  title_quote: 'Quote',
  header_layout: 'logo-left',
  show_payment_panel: true,
  show_payment_history: true,
  show_vat_column: true,
  show_banner: false,
  footer_text: 'Thank you for your business.',
  terms_text: 'Payment due by the date stated above. Late payments may incur fees.',
  greeting_text: '',
  page_size: 'A4',
};

const FONT_OPTIONS = [
  { value: 'Fraunces', label: 'Fraunces (serif, ledger feel)' },
  { value: 'Inter', label: 'Inter (sans, modern)' },
  { value: 'Playfair Display', label: 'Playfair Display (serif, editorial)' },
  { value: 'Roboto', label: 'Roboto (sans, neutral)' },
  { value: 'Georgia', label: 'Georgia (serif, classic)' },
];

export default function InvoiceDesigner() {
  const { currentOrg } = useOrg();
  const [template, setTemplate] = useState<InvoiceTemplate | null>(null);
  const [settings, setSettings] = useState<InvoiceTemplateSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const bannerInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('dodobooks_invoice_templates')
        .select('*')
        .eq('org_id', currentOrg.id)
        .eq('is_default', true)
        .maybeSingle();
      if (data) {
        setTemplate(data as InvoiceTemplate);
        setSettings({ ...DEFAULTS, ...((data as any).settings ?? {}) });
      } else {
        setTemplate(null);
        setSettings(DEFAULTS);
      }
      setLoading(false);
    })();
  }, [currentOrg]);

  function patch(p: Partial<InvoiceTemplateSettings>) {
    setSettings((s) => ({ ...s, ...p }));
  }

  async function upload(kind: 'logo' | 'banner', file: File) {
    if (!currentOrg) return;
    setBusy(true); setErr(null); setMsg(null);
    const ext = (file.name.split('.').pop() ?? 'png').toLowerCase();
    const path = `${currentOrg.id}/${kind}-${Date.now()}.${ext}`;
    const { error: uploadErr } = await supabase.storage
      .from('dodobooks-uploads')
      .upload(path, file, { contentType: file.type, upsert: false });
    if (uploadErr) { setBusy(false); setErr('Upload failed: ' + uploadErr.message); return; }
    const { data: pub } = supabase.storage.from('dodobooks-uploads').getPublicUrl(path);
    setBusy(false);
    if (kind === 'logo') patch({ logo_url: pub.publicUrl });
    else patch({ banner_url: pub.publicUrl, show_banner: true });
  }

  async function save() {
    if (!currentOrg) return;
    setBusy(true); setErr(null); setMsg(null);
    const payload = {
      org_id: currentOrg.id,
      name: template?.name ?? 'Default',
      is_default: true,
      settings: settings as any,
      updated_at: new Date().toISOString(),
    };
    if (template) {
      const { error } = await supabase.from('dodobooks_invoice_templates').update(payload).eq('id', template.id);
      if (error) { setBusy(false); setErr(error.message); return; }
    } else {
      const { data, error } = await supabase.from('dodobooks_invoice_templates').insert(payload).select('*').single();
      if (error || !data) { setBusy(false); setErr(error?.message ?? 'Could not save'); return; }
      setTemplate(data as InvoiceTemplate);
    }
    setBusy(false);
    setMsg('Saved. Every invoice & quote you send (and the public link) will now use this template.');
  }

  function resetToDefaults() {
    if (!confirm('Reset all branding to DodoBooks defaults?')) return;
    setSettings(DEFAULTS);
  }

  if (loading) return <div className="text-text-mute text-sm py-8 text-center">Loading template…</div>;
  if (!currentOrg) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
      {/* Form */}
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save(); }} className="flex flex-col gap-5">
        <div className="card">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon size={18} className="text-emerald" />
            <h2 className="h-section text-base">Logo & banner</h2>
          </div>
          <p className="text-xs text-text-mute mb-3">Uploads are public and stored in your Supabase bucket. PNG / SVG / WebP, max 5 MB.</p>

          <div className="field mb-3">
            <label>Logo URL</label>
            <div className="rounded-md border border-line bg-ink-2/40 p-2.5 mb-2 text-[11px] leading-relaxed">
              <div className="font-mono uppercase tracking-widest text-[9px] text-emerald mb-1">Recommended logo size</div>
              <div className="text-text-mute">
                <span className="text-text font-semibold">400 × 120 px</span> · transparent PNG or SVG · landscape orientation
              </div>
              <div className="text-text-mute mt-0.5">
                Renders at 48 px tall on the invoice — upload at 2× (so <span className="text-text">800 × 240 px</span>) for crisp retina / print quality.
              </div>
              <div className="text-text-mute mt-0.5">Minimum 200 × 60 px · max 5 MB</div>
            </div>
            <div className="flex gap-2">
              <input value={settings.logo_url ?? ''} onChange={(e) => patch({ logo_url: e.target.value || null })} placeholder="https://…" className="flex-1 text-xs" />
              <input ref={logoInput} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload('logo', f); }} />
              <button type="button" onClick={() => logoInput.current?.click()} disabled={busy} className="btn btn-secondary btn-sm whitespace-nowrap">
                <Upload size={12} /> Upload
              </button>
            </div>
            {settings.logo_url && (
              <div className="mt-2 p-2 bg-ink-2 rounded border border-line inline-block">
                <img src={settings.logo_url} alt="logo" className="h-12 max-w-[200px] object-contain" />
              </div>
            )}
          </div>

          <div className="field">
            <label className="flex items-center gap-2">
              <input type="checkbox" className="accent-emerald" checked={!!settings.show_banner} onChange={(e) => patch({ show_banner: e.target.checked })} />
              <span className="normal-case tracking-normal font-sans text-sm text-text">Show header banner</span>
            </label>
            {settings.show_banner && (
              <>
                <div className="rounded-md border border-line bg-ink-2/40 p-2.5 mt-2 mb-2 text-[11px] leading-relaxed">
                  <div className="font-mono uppercase tracking-widest text-[9px] text-emerald mb-1">Recommended banner size</div>
                  <div className="text-text-mute">
                    <span className="text-text font-semibold">1600 × 200 px</span> (8 : 1 aspect ratio) · JPG, PNG, or WebP
                  </div>
                  <div className="text-text-mute mt-0.5">
                    Stretches edge-to-edge across the top of the invoice at 96 px tall. Keep important details near the center — the edges may be cropped on narrow screens or when printed.
                  </div>
                  <div className="text-text-mute mt-0.5">Minimum 1200 × 150 px · max 5 MB</div>
                </div>
                <div className="flex gap-2">
                  <input value={settings.banner_url ?? ''} onChange={(e) => patch({ banner_url: e.target.value || null })} placeholder="https://…" className="flex-1 text-xs" />
                  <input ref={bannerInput} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload('banner', f); }} />
                  <button type="button" onClick={() => bannerInput.current?.click()} disabled={busy} className="btn btn-secondary btn-sm whitespace-nowrap">
                    <Upload size={12} /> Upload
                  </button>
                </div>
                {settings.banner_url && (
                  <div className="mt-2 p-2 bg-ink-2 rounded border border-line">
                    <img src={settings.banner_url} alt="banner" className="w-full max-h-20 object-cover rounded" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-2 mb-3">
            <Palette size={18} className="text-emerald" />
            <h2 className="h-section text-base">Colors</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ColorField label="Primary" value={settings.primary_color ?? '#0c8f60'} onChange={(v) => patch({ primary_color: v })} />
            <ColorField label="Accent" value={settings.accent_color ?? '#d6a85a'} onChange={(v) => patch({ accent_color: v })} />
            <ColorField label="Paper background" value={settings.paper_color ?? '#f5efe0'} onChange={(v) => patch({ paper_color: v })} />
            <ColorField label="Text" value={settings.text_color ?? '#0f1521'} onChange={(v) => patch({ text_color: v })} />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-2 mb-3">
            <Type size={18} className="text-emerald" />
            <h2 className="h-section text-base">Typography & layout</h2>
          </div>
          <div className="field mb-3">
            <label>Heading font</label>
            <select value={settings.font_family ?? 'Fraunces'} onChange={(e) => patch({ font_family: e.target.value })}>
              {FONT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="field mb-3">
            <label>Logo position</label>
            <select value={settings.header_layout ?? 'logo-left'} onChange={(e) => patch({ header_layout: e.target.value as any })}>
              <option value="logo-left">Logo left, doc info right</option>
              <option value="logo-right">Logo right, doc info left</option>
              <option value="logo-center">Logo centered</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Invoice title</label><input value={settings.title_invoice ?? ''} onChange={(e) => patch({ title_invoice: e.target.value })} /></div>
            <div className="field"><label>Quote title</label><input value={settings.title_quote ?? ''} onChange={(e) => patch({ title_quote: e.target.value })} /></div>
            <div className="field"><label>Credit note title</label><input value={settings.title_credit_note ?? ''} onChange={(e) => patch({ title_credit_note: e.target.value })} /></div>
            <div className="field"><label>Page size</label>
              <select value={settings.page_size ?? 'A4'} onChange={(e) => patch({ page_size: e.target.value as any })}>
                <option>A4</option><option>Letter</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="h-section text-base mb-3">Messaging</h2>
          <div className="field mb-3">
            <label>Top-of-document greeting (optional)</label>
            <input value={settings.greeting_text ?? ''} onChange={(e) => patch({ greeting_text: e.target.value })} placeholder="e.g. Thanks for choosing us!" />
          </div>
          <div className="field mb-3">
            <label>Footer text</label>
            <textarea rows={2} value={settings.footer_text ?? ''} onChange={(e) => patch({ footer_text: e.target.value })} />
          </div>
          <div className="field">
            <label>Terms & conditions (printed under totals)</label>
            <textarea rows={3} value={settings.terms_text ?? ''} onChange={(e) => patch({ terms_text: e.target.value })} />
          </div>
        </div>

        <div className="card">
          <h2 className="h-section text-base mb-3">Sections to show</h2>
          <div className="space-y-2">
            <Toggle label="Bank details panel (How to pay)" value={!!settings.show_payment_panel} onChange={(v) => patch({ show_payment_panel: v })} />
            <Toggle label="Payment history" value={!!settings.show_payment_history} onChange={(v) => patch({ show_payment_history: v })} />
            <Toggle label="VAT % column on line items" value={!!settings.show_vat_column} onChange={(v) => patch({ show_vat_column: v })} />
          </div>
        </div>

        {err && <div className="text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}
        {msg && <div className="text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md"><Check size={14} className="inline mr-1" />{msg}</div>}

        <div className="flex items-center justify-between sticky bottom-0 bg-ink py-3 -mx-1 px-1 border-t border-line">
          <button type="button" onClick={resetToDefaults} className="btn btn-ghost text-rust"><RotateCcw size={14} /> Reset</button>
          <button type="submit" disabled={busy} className="btn btn-primary disabled:opacity-50"><Save size={14} /> {busy ? 'Saving…' : 'Save template'}</button>
        </div>
      </form>

      {/* Preview */}
      <div className="lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
        <div className="flex items-center gap-2 mb-3">
          <Eye size={14} className="text-text-mute" />
          <span className="text-xs font-mono uppercase tracking-widest text-text-mute">Live preview — sample invoice</span>
        </div>
        <DesignPreview org={currentOrg} settings={settings} />
      </div>
    </div>
  );
}

const SWATCH_PRESETS: Record<string, string[]> = {
  Primary: ['#0c8f60', '#0a7a52', '#d97757', '#1e6fb8', '#7c3aed', '#000000', '#0f1521', '#475569'],
  Accent: ['#d6a85a', '#f59e0b', '#15c486', '#f43f5e', '#22d3ee', '#a855f7', '#ffffff', '#0f1521'],
  'Paper background': ['#f5efe0', '#ffffff', '#fafaf5', '#fef3c7', '#e0f2fe', '#f0fdf4', '#fdf2f8', '#f8fafc'],
  Text: ['#0f1521', '#000000', '#1f2937', '#374151', '#475569', '#7c2d12', '#1e40af', '#064e3b'],
};

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const hexInputId = `hex-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const colorInputRef = useRef<HTMLInputElement>(null);
  const safe = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000';
  const presets = SWATCH_PRESETS[label] ?? [];

  function handleHexChange(v: string) {
    let next = v.trim();
    if (next && !next.startsWith('#')) next = '#' + next;
    onChange(next);
  }

  return (
    <div className="field">
      <label htmlFor={hexInputId}>{label}</label>
      <div className="flex gap-2 items-stretch">
        <button
          type="button"
          onClick={() => colorInputRef.current?.click()}
          className="relative shrink-0 w-10 h-10 rounded-md border-2 border-line hover:border-emerald transition-colors cursor-pointer overflow-hidden"
          style={{ background: safe }}
          title={`Pick ${label.toLowerCase()} colour`}
          aria-label={`Pick ${label.toLowerCase()} colour`}
        >
          <input
            ref={colorInputRef}
            type="color"
            value={safe}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            tabIndex={-1}
          />
        </button>
        <input
          id={hexInputId}
          type="text"
          value={value}
          onChange={(e) => handleHexChange(e.target.value)}
          placeholder="#000000"
          maxLength={7}
          spellCheck={false}
          className="flex-1 font-mono text-xs uppercase tracking-wider min-w-0"
        />
      </div>
      {presets.length > 0 && (
        <div className="flex gap-1.5 mt-1.5 flex-wrap">
          {presets.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              className={
                'w-5 h-5 rounded-sm border transition-transform hover:scale-110 ' +
                (c.toLowerCase() === safe.toLowerCase() ? 'border-emerald ring-1 ring-emerald' : 'border-line')
              }
              style={{ background: c }}
              title={c}
              aria-label={`Use ${c}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm">
      <input type="checkbox" className="accent-emerald" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-text normal-case tracking-normal font-sans">{label}</span>
    </label>
  );
}

function DesignPreview({ org, settings }: { org: Organization; settings: InvoiceTemplateSettings }) {
  const sampleLines = useMemo(() => ([
    { description: 'Consulting — strategy workshop', qty: 4, unit_price: 1500, vat: 15, total: 6900 },
    { description: 'Travel & disbursements', qty: 1, unit_price: 450, vat: 15, total: 517.5 },
  ]), []);
  const sampleSubtotal = sampleLines.reduce((s, l) => s + l.qty * l.unit_price, 0);
  const sampleTax = sampleLines.reduce((s, l) => s + (l.qty * l.unit_price) * (l.vat / 100), 0);
  const sampleTotal = sampleSubtotal + sampleTax;

  const layout = settings.header_layout ?? 'logo-left';
  const primary = settings.primary_color ?? '#0c8f60';
  const accent = settings.accent_color ?? '#d6a85a';
  const paper = settings.paper_color ?? '#f5efe0';
  const text = settings.text_color ?? '#0f1521';
  const fontFamily = settings.font_family ?? 'Fraunces';
  const title = settings.title_invoice ?? 'Invoice';

  return (
    <article className="rounded-xl shadow-2xl overflow-hidden" style={{ background: paper, color: text, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {settings.show_banner && settings.banner_url && (
        <img src={settings.banner_url} alt="" className="w-full h-24 object-cover" />
      )}
      <div className="p-8">
        <div className={'mb-8 ' + (layout === 'logo-center' ? 'text-center' : 'grid grid-cols-2 gap-6')}>
          <div className={layout === 'logo-right' ? 'order-2 text-right' : ''}>
            {settings.logo_url
              ? <img src={settings.logo_url} alt={org.name} className={'h-14 object-contain mb-2 ' + (layout === 'logo-center' ? 'mx-auto' : '')} />
              : <div className="font-bold text-2xl mb-1" style={{ fontFamily }}>{org.name}</div>}
            <div className="text-xs leading-relaxed" style={{ color: 'rgba(15,21,33,0.7)' }}>
              {org.legal_name && org.legal_name !== org.name && <div>{org.legal_name}</div>}
              <div>{org.city || 'Vereeniging'}, South Africa</div>
              {org.vat_number && <div className="font-mono mt-1">VAT {org.vat_number}</div>}
            </div>
          </div>
          <div className={'mt-4 lg:mt-0 ' + (layout === 'logo-right' ? 'order-1 text-left' : layout === 'logo-center' ? 'mt-6' : 'text-right')}>
            <div className="font-bold text-4xl tracking-tight" style={{ fontFamily, color: text }}>{title}</div>
            <div className="font-mono text-sm mt-1" style={{ color: 'rgba(15,21,33,0.65)' }}>#INV-2026-0001</div>
            <div className="mt-4 inline-block text-left">
              <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Bill to</div>
              <div className="font-semibold mt-0.5">Sample Customer (Pty) Ltd</div>
              <div className="text-xs">jane@samplecustomer.co.za</div>
            </div>
          </div>
        </div>

        {settings.greeting_text && (
          <div className="text-sm mb-4 italic" style={{ color: 'rgba(15,21,33,0.75)' }}>{settings.greeting_text}</div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-6 py-3 border-y" style={{ borderColor: 'rgba(15,21,33,0.15)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Issued</div>
            <div className="font-semibold text-sm mt-0.5">{formatDate(new Date())}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Due</div>
            <div className="font-semibold text-sm mt-0.5">{formatDate(new Date(Date.now() + 30 * 86400000))}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Reference</div>
            <div className="font-semibold text-sm mt-0.5 font-mono">PO-12345</div>
          </div>
        </div>

        <table className="w-full mb-4">
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(15,21,33,0.18)' }}>
              <th className="text-left py-2 text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Description</th>
              <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-14" style={{ color: 'rgba(15,21,33,0.55)' }}>Qty</th>
              <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-24" style={{ color: 'rgba(15,21,33,0.55)' }}>Unit</th>
              {settings.show_vat_column && <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-14" style={{ color: 'rgba(15,21,33,0.55)' }}>VAT</th>}
              <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-24" style={{ color: 'rgba(15,21,33,0.55)' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {sampleLines.map((l, i) => (
              <tr key={i} style={{ borderBottom: '1px dashed rgba(15,21,33,0.1)' }}>
                <td className="py-2 text-sm align-top">{l.description}</td>
                <td className="py-2 text-right font-mono text-sm">{l.qty}</td>
                <td className="py-2 text-right font-mono text-sm">{formatMoney(l.unit_price)}</td>
                {settings.show_vat_column && <td className="py-2 text-right font-mono text-xs" style={{ color: 'rgba(15,21,33,0.65)' }}>{l.vat}%</td>}
                <td className="py-2 text-right font-mono text-sm font-semibold">{formatMoney(l.qty * l.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-64 text-sm">
            <div className="flex justify-between py-1"><span style={{ color: 'rgba(15,21,33,0.65)' }}>Subtotal</span><span className="font-mono">{formatMoney(sampleSubtotal)}</span></div>
            <div className="flex justify-between py-1 pb-2" style={{ borderBottom: '1px solid rgba(15,21,33,0.15)' }}><span style={{ color: 'rgba(15,21,33,0.65)' }}>VAT</span><span className="font-mono">{formatMoney(sampleTax)}</span></div>
            <div className="flex justify-between py-2 font-bold text-xl" style={{ fontFamily, borderBottom: `2px solid ${primary}` }}>
              <span>Total</span><span className="font-mono" style={{ color: primary }}>{formatMoney(sampleTotal)}</span>
            </div>
          </div>
        </div>

        {settings.show_payment_panel && (
          <div className="mt-6 rounded-lg p-4" style={{ background: `${primary}15`, border: `1px solid ${primary}50` }}>
            <div className="font-semibold mb-2" style={{ color: primary, fontFamily }}>How to pay</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest font-mono opacity-60">Bank</div><div className="font-semibold">FNB</div></div>
              <div><div className="text-[10px] uppercase tracking-widest font-mono opacity-60">Account</div><div className="font-mono">62123456789</div></div>
              <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest font-mono opacity-60">Reference</div><div className="font-mono font-bold">INV-2026-0001</div></div>
            </div>
          </div>
        )}

        {settings.terms_text && (
          <div className="mt-6 pt-3 text-xs" style={{ color: 'rgba(15,21,33,0.7)', borderTop: '1px solid rgba(15,21,33,0.12)' }}>
            <div className="text-[10px] uppercase tracking-widest font-mono mb-1" style={{ color: 'rgba(15,21,33,0.55)' }}>Terms</div>
            {settings.terms_text}
          </div>
        )}

        {settings.footer_text && (
          <div className="mt-4 pt-3 text-center text-xs italic" style={{ color: 'rgba(15,21,33,0.55)', borderTop: '1px solid rgba(15,21,33,0.08)' }}>
            {settings.footer_text}
          </div>
        )}

        <div className="mt-4 h-1 rounded" style={{ background: `linear-gradient(90deg, ${primary} 0%, ${accent} 100%)` }} />
      </div>
    </article>
  );
}
